import * as XLSX from 'xlsx';
import { UNIT_OPTIONS, isCanonicalUnit } from './constants';

/**
 * Column definitions for the product import/export template.
 * `header` is the Excel column name, `field` is the API field name used in POST /products.
 */
const COLUMNS = [
  {
    header: 'name',
    field: 'name',
    required: true,
    description: 'The product name. This field is required. Rows without a name will be skipped during import.',
  },
  {
    header: 'displayName',
    field: 'displayName',
    description: 'An optional display name shown on the POS screen. If left blank, the product name is used instead.',
  },
  {
    header: 'primaryBarcode',
    field: 'primaryBarcode',
    required: true,
    description:
      'The primary barcode or UPC for the product. If a barcode already exists in the system, the product will be updated rather than duplicated.',
  },
  {
    header: 'additionalBarcodes',
    field: 'additionalBarcodes',
    description: 'Any extra barcodes for the same product, separated by commas (e.g. "012345678901, 012345678902").',
  },
  { header: 'sku', field: 'sku', description: 'The internal stock keeping unit code used to identify the product.' },
  {
    header: 'sellingPrice',
    field: 'sellingPrice',
    description: 'The retail selling price as a number (e.g. 2.99). Leave blank if not applicable.',
  },
  {
    header: 'cost',
    field: 'cost',
    description: 'The cost price paid to the supplier as a number (e.g. 1.50). Leave blank if not applicable.',
  },
  {
    header: 'crvFee',
    field: 'crvFee',
    description:
      'California Refund Value (CRV) deposit in cents. Use 0, 5, 10, 15, 20, 25, 50, 60, or 75. Leave blank for no CRV.',
  },
  {
    header: 'expiryDate',
    field: 'expiryDate',
    description:
      "Expiry date 1 at the import's location, as YYYY-MM-DD (e.g. 2026-12-31). Leave blank if the product doesn't expire.",
  },
  {
    header: 'batchNo1',
    field: 'batchNo1',
    description: 'Batch number for expiry date 1 (e.g. BATCH1). Optional.',
  },
  {
    header: 'expiryDate2',
    field: 'expiryDate2',
    description: 'Expiry date 2 as YYYY-MM-DD, for a second batch on the shelf. Optional.',
  },
  {
    header: 'batchNo2',
    field: 'batchNo2',
    description: 'Batch number for expiry date 2. Optional.',
  },
  // Vendor 1
  {
    header: 'vendor1Name',
    field: 'vendor1Name',
    description:
      'First vendor / supplier name. Auto-created if the name does not exist. Leave blank if the product has no vendors. A product can have multiple vendors — repeat with vendor2Name, vendor3Name, etc.',
  },
  {
    header: 'vendor1Sku',
    field: 'vendor1Sku',
    description: "Vendor 1's own item code or SKU for this product. Leave blank if not applicable.",
  },
  {
    header: 'vendor1ProductName',
    field: 'vendor1ProductName',
    description: 'The product name as used by Vendor 1 (optional).',
  },
  {
    header: 'vendor1Price',
    field: 'vendor1Price',
    description:
      "Vendor 1's cost for this product as a number (e.g. 1.25). The tenant compares vendor costs to choose where to order from.",
  },
  {
    header: 'vendor1Unit',
    field: 'vendor1Unit',
    description:
      'Unit Vendor 1 sells in. Allowed values: "each", "case", "pallet". Defaults to "each" if left blank when a vendor name is set.',
  },
  {
    header: 'vendor1Reason',
    field: 'vendor1Reason',
    description:
      'Reason for Vendor 1\'s price (logged to vendor price history). Allowed values: "Promotion", "Price Increase", "Price Decrease", "New Contract", "Miscellaneous". Leave blank for none.',
  },
  {
    header: 'vendor1EffectiveDate',
    field: 'vendor1EffectiveDate',
    description:
      "Date Vendor 1's price takes effect, as YYYY-MM-DD. Defaults to today if left blank when a vendor price is set.",
  },
  // Vendor 2
  {
    header: 'vendor2Name',
    field: 'vendor2Name',
    description: 'Second vendor name (optional). Same rules as vendor1Name.',
  },
  {
    header: 'vendor2Sku',
    field: 'vendor2Sku',
    description: "Vendor 2's item code (optional).",
  },
  {
    header: 'vendor2ProductName',
    field: 'vendor2ProductName',
    description: 'The product name as used by Vendor 2 (optional).',
  },
  {
    header: 'vendor2Price',
    field: 'vendor2Price',
    description: "Vendor 2's cost (optional).",
  },
  {
    header: 'vendor2Unit',
    field: 'vendor2Unit',
    description: "Vendor 2's unit (optional).",
  },
  {
    header: 'vendor2Reason',
    field: 'vendor2Reason',
    description: "Reason for Vendor 2's price (optional). Same allowed values as vendor1Reason.",
  },
  {
    header: 'vendor2EffectiveDate',
    field: 'vendor2EffectiveDate',
    description: "Date Vendor 2's price takes effect (optional). Same format as vendor1EffectiveDate.",
  },
  // Vendor 3
  {
    header: 'vendor3Name',
    field: 'vendor3Name',
    description:
      'Third vendor name (optional). Same rules as vendor1Name. To add more, append vendor4Name, vendor5Name, etc.',
  },
  {
    header: 'vendor3Sku',
    field: 'vendor3Sku',
    description: "Vendor 3's item code (optional).",
  },
  {
    header: 'vendor3ProductName',
    field: 'vendor3ProductName',
    description: 'The product name as used by Vendor 3 (optional).',
  },
  {
    header: 'vendor3Price',
    field: 'vendor3Price',
    description: "Vendor 3's cost (optional).",
  },
  {
    header: 'vendor3Unit',
    field: 'vendor3Unit',
    description: "Vendor 3's unit (optional).",
  },
  {
    header: 'vendor3Reason',
    field: 'vendor3Reason',
    description: "Reason for Vendor 3's price (optional). Same allowed values as vendor1Reason.",
  },
  {
    header: 'vendor3EffectiveDate',
    field: 'vendor3EffectiveDate',
    description: "Date Vendor 3's price takes effect (optional). Same format as vendor1EffectiveDate.",
  },
  {
    header: 'departmentName',
    field: 'departmentName',
    description:
      'The department this product belongs to (e.g. "Dairy", "Produce"). It must already exist in POS Management → Departments — rows naming an unknown department are not imported.',
  },
  {
    header: 'categoryNames',
    field: 'categoryNames',
    description:
      'One or more category names separated by commas (e.g. "Snacks, Organic"). Each must already exist in POS Management → Categories — rows naming an unknown category are not imported.',
  },
  {
    header: 'inStockQty',
    field: 'inStockQty',
    description: 'The current inventory count as a whole number (e.g. 100). Defaults to 0 if left blank.',
  },
  {
    header: 'reorderThreshold',
    field: 'reorderThreshold',
    description:
      'The low stock alert level. When inventory falls to or below this number, a low stock warning is triggered.',
  },
  {
    header: 'reorderQuantity',
    field: 'reorderQuantity',
    description:
      'How many units to order when stock falls to or below the reorder threshold. Used to auto-fill purchase order quantities.',
  },
  {
    header: 'soldByWeight',
    field: 'soldByWeight',
    description:
      'Enter "Yes" if this product is sold by weight (e.g. produce, meat). Enter "No" or leave blank otherwise.',
  },
  {
    header: 'ageVerification',
    field: 'ageVerification',
    description:
      'Enter "Yes" if the cashier should be prompted to verify the customer\'s age (e.g. alcohol, tobacco). Enter "No" or leave blank otherwise.',
  },
  {
    header: 'ebtEligible',
    field: 'ebtEligible',
    description: 'Enter "Yes" if this product can be purchased with EBT/SNAP. Defaults to "Yes" if left blank.',
  },
  {
    header: 'couponEligible',
    field: 'couponEligible',
    description: 'Enter "Yes" if coupons/discounts may be applied to this product. Defaults to "Yes" if left blank.',
  },
  {
    header: 'taxable',
    field: 'taxable',
    description: 'Enter "Yes" if sales tax applies to this product. Defaults to "Yes" if left blank.',
  },
  {
    header: 'Kitchen Printing / KDS',
    field: 'kitchenPrinting',
    description:
      'Enter "Yes" if this product should print to the kitchen / show on the KDS. Enter "No" to keep it off the kitchen display. Defaults to "Yes" if left blank.',
  },
  {
    header: 'isVariablePrice',
    field: 'isVariablePrice',
    description:
      'Enter "Yes" if the cashier is allowed to override the price at checkout (e.g. gift cards). Enter "No" or leave blank otherwise.',
  },
  {
    header: 'promptQuantity',
    field: 'promptQuantity',
    description:
      'Enter "Yes" if the cashier should be prompted to enter a quantity when adding this product. Enter "No" or leave blank otherwise.',
  },
  {
    header: 'defaultSellingUnit',
    field: 'defaultSellingUnit',
    description:
      'The unit used when selling this product. Allowed values: "each", "case", "pallet". Defaults to "each" if left blank.',
  },
  {
    header: 'defaultPurchasingUnit',
    field: 'defaultPurchasingUnit',
    description:
      'The unit used when purchasing this product from suppliers. Allowed values: "each", "case", "pallet". Defaults to "each" if left blank. When set to "case" the "Units in Case", "Cost per Case ($)", and "Selling Price per Case ($)" columns must be filled. When set to "pallet" use the corresponding "...per Pallet" columns instead.',
  },
  {
    header: 'Units in Case',
    field: 'unitsInCase',
    description:
      'How many base ("each") units / weight units make up one case. Required when defaultPurchasingUnit is "case" — without it the case unit row cannot be created and the row is rejected.',
  },
  {
    header: 'Cost per Case ($)',
    field: 'costPerCase',
    description:
      'The cost paid to the supplier for one full case as a number (e.g. 24.00). Optional; used together with "Units in Case" to populate the case unit row.',
  },
  {
    header: 'Selling Price per Case ($)',
    field: 'sellingPricePerCase',
    description:
      'Selling Price for one full case. Required when defaultPurchasingUnit is "case" — POs and warehouse receives price against this.',
  },
  {
    header: 'Units in Pallet',
    field: 'unitsInPallet',
    description:
      'How many base ("each") units / weight units make up one pallet. Required when defaultPurchasingUnit is "pallet" — without it the pallet unit row cannot be created and the row is rejected.',
  },
  {
    header: 'Cost per Pallet ($)',
    field: 'costPerPallet',
    description:
      'The cost paid to the supplier for one full pallet as a number. Optional; used together with "Units in Pallet" to populate the pallet unit row.',
  },
  {
    header: 'Selling Price per Pallet ($)',
    field: 'sellingPricePerPallet',
    description:
      'Selling Price for one full pallet. Required when defaultPurchasingUnit is "pallet" — POs and warehouse receives price against this.',
  },
  {
    header: 'building',
    field: 'building',
    description:
      'The building this product is stocked in, for sites with more than one (e.g. "B1", "Warehouse 2"). Building, aisle and bin together give the full location.',
  },
  {
    header: 'aisle',
    field: 'aisle',
    description: 'The aisle location where this product is stocked in the store (e.g. "A3", "Aisle 5").',
  },
  { header: 'bin', field: 'bin', description: 'The bin or bay location within the aisle (e.g. "B2", "Shelf 3").' },
  {
    header: 'Ingredients Notes',
    field: 'customField1',
    description:
      'Optional ingredients notes printed on the mobile app label printer (free text, up to 500 characters).',
  },
  {
    header: 'Original Price',
    field: 'customField2',
    description: 'Optional original price (free text, up to 500 characters).',
  },
  {
    header: 'Custom Field',
    field: 'customField3',
    description: 'Optional custom field (free text, up to 500 characters).',
  },
  {
    header: 'Created By',
    field: 'createdBy',
    description:
      'Who created this product (system-derived: a user name, or Import / Analytics / a POS user-register). Read-only — this column is ignored on import.',
  },
  {
    header: 'Changed By',
    field: 'changedBy',
    description:
      'Who last changed this product (system-derived, same rules as Created By). Read-only — this column is ignored on import.',
  },
  {
    header: 'Modified',
    field: 'lastChangedAt',
    description:
      'When this product was last changed by the person or import named in Changed By (ISO 8601 UTC). Read-only — this column is ignored on import: the server stamps it.',
  },
] as const;

const HEADERS = COLUMNS.map((c) => c.header);

function boolToYesNo(val: unknown): string {
  return val ? 'Yes' : 'No';
}

/** Export all products as an XLSX file. */
export function exportProducts(products: any[], filename = 'products_export.xlsx') {
  const rows = products.map((p) => {
    // Spread up to the first three productPayees rows into vendor1*..vendor3*
    // so the round-trip (export → edit → import) preserves vendor data.
    const payees = Array.isArray(p.payees) ? p.payees : [];
    const v = (idx: number) => payees[idx] ?? null;
    const cellsForVendor = (idx: number) => {
      const x = v(idx);
      // Reason + effectiveDate live on the vendorPriceHistory table, not on
      // productPayees. We export blank for those on the round-trip — the
      // import treats blanks as "no new history entry" so existing history
      // is left alone.
      return [
        x?.payeeName ?? x?.name ?? '',
        x?.payeeItemCode ?? '',
        x?.vendorProductName ?? '',
        x?.vendorPrice != null ? Number(x.vendorPrice) : '',
        x?.unitName ?? '',
        '',
        '',
      ];
    };
    // Pull the case + pallet unit rows so the round-trip preserves baseQty
    // + cost + sellingPrice for whichever multi-pack unit the product uses.
    // Match by unitName, case-insensitive, tolerating the singular/plural
    // variants the import accepts.
    const units = Array.isArray(p.units) ? p.units : [];
    const findUnit = (target: string) =>
      units.find((u: any) => {
        const n = String(u?.unitName ?? '')
          .trim()
          .toLowerCase();
        return n === target || n === `${target}s`;
      });
    const caseRow = findUnit('case');
    const palletRow = findUnit('pallet');
    // Sold-by-weight items always sell as "each" — keep export aligned with
    // the form's lock so re-imports don't try to set a stale value.
    const sellingUnitForExport = p.soldByWeight ? 'each' : (p.defaultSellingUnit ?? 'each');
    return [
      p.name ?? '',
      p.displayName ?? '',
      p.primaryBarcode ?? '',
      Array.isArray(p.additionalBarcodes) ? p.additionalBarcodes.join(', ') : '',
      p.sku ?? '',
      p.sellingPrice != null ? Number(p.sellingPrice) : '',
      p.cost != null ? Number(p.cost) : '',
      p.crvFee != null ? Number(p.crvFee) : '',
      p.expiryDate ? String(p.expiryDate).slice(0, 10) : '',
      p.batchNo1 ?? '',
      p.expiryDate2 ? String(p.expiryDate2).slice(0, 10) : '',
      p.batchNo2 ?? '',
      ...cellsForVendor(0),
      ...cellsForVendor(1),
      ...cellsForVendor(2),
      p.departmentName ?? '',
      p.categoryNames ?? '',
      p.inStockQty ?? 0,
      p.reorderThreshold ?? '',
      p.reorderQuantity ?? '',
      boolToYesNo(p.soldByWeight),
      boolToYesNo(p.ageVerification),
      boolToYesNo(p.ebtEligible),
      boolToYesNo(p.couponEligible),
      boolToYesNo(p.taxable),
      // "Kitchen Printing / KDS" is the positive sense: Yes = prints to the
      // kitchen / shows on the KDS. We persist the negative excludeKitchenPrint,
      // so invert on export.
      boolToYesNo(!p.excludeKitchenPrint),
      boolToYesNo(p.isVariablePrice),
      boolToYesNo(p.promptQuantity),
      sellingUnitForExport,
      p.defaultPurchasingUnit ?? 'each',
      caseRow?.baseQty != null ? Number(caseRow.baseQty) : '',
      caseRow?.cost != null ? Number(caseRow.cost) : '',
      caseRow?.sellingPrice != null ? Number(caseRow.sellingPrice) : '',
      palletRow?.baseQty != null ? Number(palletRow.baseQty) : '',
      palletRow?.cost != null ? Number(palletRow.cost) : '',
      palletRow?.sellingPrice != null ? Number(palletRow.sellingPrice) : '',
      p.building ?? '',
      p.aisle ?? '',
      p.bin ?? '',
      p.customField1 ?? '',
      p.customField2 ?? '',
      p.customField3 ?? '',
      // Read-only attribution (system-derived; ignored on import).
      p.createdBy ?? '',
      p.changedBy ?? '',
      (p.lastChangedAt ?? p.updatedAt) ? new Date(p.lastChangedAt ?? p.updatedAt).toISOString() : '',
    ];
  });

  const wb = XLSX.utils.book_new();
  const wsData = [HEADERS, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  ws['!cols'] = HEADERS.map((h) => ({ wch: Math.max(h.length + 4, 14) }));

  // Force identifier columns to text format so Excel doesn't autoconvert
  // values that look like dates (sku "3/1/26" → Mar 1 2026) or strip
  // leading zeros / round to scientific notation (long barcodes). The "@"
  // numFmt tells Excel to render the cell literally.
  const textColumns: readonly string[] = ['sku', 'primaryBarcode', 'additionalBarcodes'];
  for (const colName of textColumns) {
    const colIdx = (HEADERS as readonly string[]).indexOf(colName);
    if (colIdx < 0) continue;
    for (let rowIdx = 1; rowIdx < wsData.length; rowIdx++) {
      const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
      const cell = ws[cellRef];
      if (!cell || cell.v === '' || cell.v == null) continue;
      cell.t = 's';
      cell.v = String(cell.v);
      cell.w = cell.v;
      cell.z = '@';
    }
  }

  XLSX.utils.book_append_sheet(wb, ws, 'Products');

  // Instructions sheet
  const instrData = [
    ['Field Name', 'Required', 'Description'],
    ...COLUMNS.map((c) => [c.header, 'required' in c && c.required ? 'Yes' : 'No', c.description]),
    [],
    ['IMPORTANT NOTES'],
    ['', '', 'Any row without a product name will be skipped during import.'],
    [
      '',
      '',
      'If a primary barcode already exists in the system, the matching product will be updated with the new values instead of creating a duplicate.',
    ],
    [
      '',
      '',
      'Departments, categories and vendors are matched by name (ignoring capitalisation and extra spaces) and are never created by an import. Create them first; rows naming one that does not exist are listed as not imported.',
    ],
    [
      '',
      '',
      'For Yes/No fields, the following values are accepted: Yes, No, TRUE, FALSE, 1, or 0. Leaving a field blank uses the default value.',
    ],
    [
      '',
      '',
      'Column headers are matched by name and are case-insensitive. Do not rename or reorder the column headers.',
    ],
    [
      '',
      '',
      'The sample data rows in the Products sheet can be deleted or overwritten with real product data before importing.',
    ],
  ];
  const instrWs = XLSX.utils.aoa_to_sheet(instrData);
  instrWs['!cols'] = [{ wch: 24 }, { wch: 10 }, { wch: 60 }];
  XLSX.utils.book_append_sheet(wb, instrWs, 'Instructions');

  XLSX.writeFile(wb, filename);
}

/** Canonical form of a department / category name: surrounding whitespace
 *  dropped and inner runs (including the non-breaking spaces Excel and web
 *  copy-paste carry) collapsed to one space. Import matches these names
 *  against what exists, so a stray trailing space on either side used to make
 *  "SNACKS" in the file miss the "SNACKS " the user had created. */
export function normalizeCatalogName(val: unknown): string {
  return String(val ?? '')
    .replace(/[\s\u200b]+/g, ' ')
    .trim();
}

/** Download a blank template with headers + instructions (no product data). */
export function exportBlankTemplate() {
  exportProducts([], 'products_import_template.xlsx');
}

/** Parse a boolean-ish cell value. */
function parseBool(val: unknown, defaultValue = false): boolean {
  if (val == null || val === '') return defaultValue;
  const s = String(val).trim().toLowerCase();
  return s === 'yes' || s === 'true' || s === '1';
}

function parseNum(val: unknown): number | undefined {
  if (val == null || val === '') return undefined;
  const n = Number(val);
  return isNaN(n) ? undefined : n;
}

/** Excel may give us a real Date (when the cell was typed as a date) or a
 *  plain YYYY-MM-DD string (when typed as text). Normalize to YYYY-MM-DD. */
function parseExpiryDate(val: unknown): string | undefined {
  if (val == null || val === '') return undefined;
  if (val instanceof Date) {
    if (isNaN(val.getTime())) return undefined;
    return val.toISOString().slice(0, 10);
  }
  const s = String(val).trim();
  if (!s) return undefined;
  // Already ISO? Take the first 10 chars.
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  return isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
}

export interface ParsedProduct {
  name: string;
  displayName?: string;
  primaryBarcode?: string;
  additionalBarcodes?: string[];
  sku?: string;
  sellingPrice?: number;
  cost?: number;
  /** California Refund Value in cents (0, 5, 10, 15, 20, 25). */
  crvFee?: number;
  /** YYYY-MM-DD if present. */
  expiryDate?: string;
  /** Second batch/expiry pair. Undefined when the file has no such column —
   *  the server then leaves the stored value alone; null clears it. */
  batchNo1?: string | null;
  expiryDate2?: string | null;
  batchNo2?: string | null;
  /** Vendors linked to this product. Each entry carries that vendor's own cost. */
  vendors?: Array<{
    name: string;
    sku?: string;
    vendorProductName?: string;
    price?: number;
    unit?: string;
    /** Reason for the price (logged to vendorPriceHistory when a price is set). */
    priceReason?: string;
    /** Effective date for the price as YYYY-MM-DD. Defaults to today on the server. */
    effectiveDate?: string;
  }>;
  departmentName?: string;
  categoryNames?: string[];
  inStockQty?: number;
  reorderThreshold?: number;
  reorderQuantity?: number;
  soldByWeight: boolean;
  ageVerification: boolean;
  ebtEligible: boolean;
  couponEligible: boolean;
  taxable: boolean;
  excludeKitchenPrint: boolean;
  isVariablePrice: boolean;
  promptQuantity: boolean;
  defaultSellingUnit?: string;
  defaultPurchasingUnit?: string;
  /** Synthesized from the purchasing-unit columns so the products service can
   *  validate defaults against, and persist, the per-product unit rows. */
  units?: Array<{ unitName: string; baseQty: number; cost?: number; sellingPrice?: number }>;
  building?: string;
  aisle?: string;
  bin?: string;
  customField1?: string;
  customField2?: string;
  customField3?: string;
}

export interface ParseResult {
  products: ParsedProduct[];
  skipped: number;
  errors: string[];
}

/** Parse an XLSX file into product objects ready for API submission. */
export async function parseProductsFromFile(file: File): Promise<ParseResult> {
  const buffer = await file.arrayBuffer();
  const wb = XLSX.read(buffer);

  const products: ParsedProduct[] = [];
  const errors: string[] = [];
  let skipped = 0;

  for (const sheetName of wb.SheetNames) {
    if (sheetName.toLowerCase() === 'instructions') continue;

    const ws = wb.Sheets[sheetName];
    const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
    if (rows.length < 2) continue;

    // Build column index from header row (case-insensitive). Lookups accept
    // either the column's `header` (as it appears in the sheet) or its `field`
    // identifier — the two differ for columns like baseUnitsPerCase whose
    // human-readable header is "Number of Base Units in a Case".
    const headerRow = rows[0].map((h: any) =>
      String(h ?? '')
        .trim()
        .toLowerCase(),
    );
    const fieldToHeader = new Map(COLUMNS.map((c) => [c.field.toLowerCase(), c.header.toLowerCase()]));
    const colIdx = (name: string) => {
      const key = name.toLowerCase();
      const headerKey = fieldToHeader.get(key) ?? key;
      const i = headerRow.indexOf(headerKey);
      if (i !== -1) return i;
      // Back-compat: also accept the column's `field` identifier as a header, so
      // templates exported before a header was renamed (e.g. "customField1"
      // before it became "Ingredients Notes") still import cleanly.
      return headerRow.indexOf(key);
    };

    const nameIdx = colIdx('name');
    if (nameIdx === -1) {
      errors.push(`Sheet "${sheetName}": missing "name" column`);
      continue;
    }

    // Discover all vendorN* column groups dynamically. The template ships with
    // vendor1/vendor2/vendor3 but a tenant can add more by extending headers.
    const vendorNumbers: number[] = [];
    for (const h of headerRow) {
      const match = String(h).match(/^vendor(\d+)name$/);
      if (match) {
        const n = parseInt(match[1], 10);
        if (!Number.isNaN(n) && !vendorNumbers.includes(n)) vendorNumbers.push(n);
      }
    }
    vendorNumbers.sort((a, b) => a - b);

    for (let r = 1; r < rows.length; r++) {
      const row = rows[r];
      const get = (field: string) => {
        const i = colIdx(field);
        if (i === -1 || i >= row.length) return undefined;
        const v = row[i];
        return v == null || v === '' ? undefined : v;
      };

      const name = String(get('name') ?? '').trim();
      if (!name) {
        skipped++;
        continue;
      }

      // The Excel row number that the user sees. Header is row 1, so the
      // first data row is row 2.
      const excelRow = r + 1;

      // Mirror the product-form's required-field rules so the importer
      // doesn't silently produce half-configured products. The backend has
      // its own checks but they're less specific — surfacing the failing
      // column inline saves the user a guess-and-retry loop.
      const primaryBarcodeRaw = get('primaryBarcode') ? String(get('primaryBarcode')).trim() : '';
      if (!primaryBarcodeRaw) {
        errors.push(`Row ${excelRow}: primaryBarcode is required`);
        skipped++;
        continue;
      }
      const productSellingPrice = parseNum(get('sellingPrice'));
      if (productSellingPrice == null || productSellingPrice < 0) {
        errors.push(`Row ${excelRow}: sellingPrice is required and must be 0 or greater`);
        skipped++;
        continue;
      }

      const additionalBarcodesRaw = get('additionalBarcodes');
      const additionalBarcodes = additionalBarcodesRaw
        ? String(additionalBarcodesRaw)
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean)
        : undefined;

      // Collect any non-empty vendor entries from the discovered groups
      const vendors: Array<{
        name: string;
        sku?: string;
        vendorProductName?: string;
        price?: number;
        unit?: string;
        priceReason?: string;
        effectiveDate?: string;
      }> = [];
      for (const n of vendorNumbers) {
        const vNameRaw = get(`vendor${n}Name`);
        if (!vNameRaw) continue;
        const vName = String(vNameRaw).trim();
        if (!vName) continue;
        const vSku = get(`vendor${n}Sku`) ? String(get(`vendor${n}Sku`)).trim() : undefined;
        const vProductName = get(`vendor${n}ProductName`) ? String(get(`vendor${n}ProductName`)).trim() : undefined;
        const vPrice = parseNum(get(`vendor${n}Price`));
        const vUnit = get(`vendor${n}Unit`) ? String(get(`vendor${n}Unit`)).trim() : undefined;
        const vReason = get(`vendor${n}Reason`) ? String(get(`vendor${n}Reason`)).trim() : undefined;
        // Effective date column may come back as a real Date (when Excel
        // typed the cell as a date) or a YYYY-MM-DD string. Reuse the same
        // normalizer the expiry date column uses so both behave the same.
        const vEffectiveDate = parseExpiryDate(get(`vendor${n}EffectiveDate`));
        vendors.push({
          name: vName,
          sku: vSku,
          vendorProductName: vProductName,
          price: vPrice,
          unit: vUnit,
          priceReason: vReason,
          effectiveDate: vEffectiveDate,
        });
      }

      // Resolve unit-of-measure defaults. Both default to "each" — that's
      // what the form does and what the backend defaults to. Reject any
      // value outside the canonical UNIT_OPTIONS so a typo (e.g. "kg")
      // doesn't sneak through.
      const defaultPurchasingUnitRaw = get('defaultPurchasingUnit') ? String(get('defaultPurchasingUnit')).trim() : '';
      const defaultSellingUnitRaw = get('defaultSellingUnit') ? String(get('defaultSellingUnit')).trim() : '';
      // Lower-cased, not just trimmed. UNIT_OPTIONS, the dropdown on the
      // product form and the unit rows synthesised below are all lower-case, so
      // sending "CASE" through verbatim stores a value that matches none of
      // them — the product then reads back as though no purchasing unit had
      // been set at all. isCanonicalUnit already compares case-insensitively,
      // which is why the mismatch got this far without an error.
      const defaultPurchasingUnit = (defaultPurchasingUnitRaw || 'each').toLowerCase();
      let defaultSellingUnit = (defaultSellingUnitRaw || 'each').toLowerCase();
      if (defaultPurchasingUnitRaw && !isCanonicalUnit(defaultPurchasingUnitRaw)) {
        errors.push(
          `Row ${excelRow}: defaultPurchasingUnit "${defaultPurchasingUnitRaw}" is not allowed. Use one of: ${UNIT_OPTIONS.join(', ')}.`,
        );
        skipped++;
        continue;
      }
      if (defaultSellingUnitRaw && !isCanonicalUnit(defaultSellingUnitRaw)) {
        errors.push(
          `Row ${excelRow}: defaultSellingUnit "${defaultSellingUnitRaw}" is not allowed. Use one of: ${UNIT_OPTIONS.join(', ')}.`,
        );
        skipped++;
        continue;
      }

      // Sold-by-weight items are always sold as "each" — coerce silently
      // rather than fail the row, mirroring the form's behavior (the form
      // disables the dropdown and forces 'each').
      const soldByWeight = parseBool(get('soldByWeight'));
      if (soldByWeight && defaultSellingUnit.toLowerCase() !== 'each') {
        errors.push(
          `Row ${excelRow}: Sold By Weight is on — defaultSellingUnit coerced to "each" (was "${defaultSellingUnit}").`,
        );
        defaultSellingUnit = 'each';
      }

      // Selling unit must be at or below the purchasing unit on the size
      // hierarchy (each < case < pallet). Rejecting the row matches the
      // form's submit guard so a user can't import a combo they can't save
      // through the UI.
      if (!soldByWeight) {
        const sellIdx = (UNIT_OPTIONS as readonly string[]).indexOf(defaultSellingUnit.toLowerCase());
        const purchIdx = (UNIT_OPTIONS as readonly string[]).indexOf(defaultPurchasingUnit.toLowerCase());
        if (sellIdx >= 0 && purchIdx >= 0 && sellIdx > purchIdx) {
          errors.push(
            `Row ${excelRow}: defaultSellingUnit "${defaultSellingUnit}" can't be larger than defaultPurchasingUnit "${defaultPurchasingUnit}".`,
          );
          skipped++;
          continue;
        }
      }

      // Kitchen printing / KDS. The template column is the positive
      // "Kitchen Printing / KDS" (Yes = prints to the kitchen, default Yes),
      // but we persist the negative excludeKitchenPrint — so invert.
      // Older exported files used the negative "excludeKitchenPrint" header
      // directly; fall back to it so re-imports of pre-existing templates
      // don't silently flip every product's kitchen flag.
      const kdsRaw = get('kitchenPrinting');
      let excludeKitchenPrint: boolean;
      if (kdsRaw !== undefined) {
        excludeKitchenPrint = !parseBool(kdsRaw, true);
      } else {
        const legacyIdx = headerRow.indexOf('excludekitchenprint');
        const legacyVal = legacyIdx !== -1 && legacyIdx < row.length ? row[legacyIdx] : undefined;
        excludeKitchenPrint = parseBool(legacyVal === '' ? undefined : legacyVal);
      }

      // Synthesize units[] entries for case + pallet from the dedicated
      // column groups. A product can carry both a case and a pallet row
      // (some warehouses receive in pallets but resell in cases), so we
      // emit whichever groups have values. The unit row whose name matches
      // defaultPurchasingUnit must be present — that's the row receivers
      // and POs price against.
      const unitsInCase = parseNum(get('unitsInCase'));
      const costPerCase = parseNum(get('costPerCase'));
      const sellingPricePerCase = parseNum(get('sellingPricePerCase'));
      const unitsInPallet = parseNum(get('unitsInPallet'));
      const costPerPallet = parseNum(get('costPerPallet'));
      const sellingPricePerPallet = parseNum(get('sellingPricePerPallet'));
      const units: Array<{ unitName: string; baseQty: number; cost?: number; sellingPrice?: number }> = [];
      if (unitsInCase != null && unitsInCase > 0) {
        units.push({
          unitName: 'case',
          baseQty: unitsInCase,
          ...(costPerCase != null ? { cost: costPerCase } : {}),
          ...(sellingPricePerCase != null ? { sellingPrice: sellingPricePerCase } : {}),
        });
      }
      if (unitsInPallet != null && unitsInPallet > 0) {
        units.push({
          unitName: 'pallet',
          baseQty: unitsInPallet,
          ...(costPerPallet != null ? { cost: costPerPallet } : {}),
          ...(sellingPricePerPallet != null ? { sellingPrice: sellingPricePerPallet } : {}),
        });
      }

      // Mirror the form's "case/pallet requires baseQty + sellingPrice" rule.
      // The backend's validateDefaultUnitsAgainstUnits catches the missing
      // baseQty but not the missing sellingPrice; this catches both inline
      // so the importer reports the exact column that's empty.
      const purchasingUnitNormalized = defaultPurchasingUnit.toLowerCase();
      if (purchasingUnitNormalized === 'case') {
        if (unitsInCase == null || unitsInCase <= 0) {
          errors.push(`Row ${excelRow}: "Units in Case" is required when defaultPurchasingUnit is "case".`);
          skipped++;
          continue;
        }
        if (sellingPricePerCase == null || sellingPricePerCase < 0) {
          errors.push(
            `Row ${excelRow}: "Selling Price per Case ($)" is required when defaultPurchasingUnit is "case".`,
          );
          skipped++;
          continue;
        }
      }
      if (purchasingUnitNormalized === 'pallet') {
        if (unitsInPallet == null || unitsInPallet <= 0) {
          errors.push(`Row ${excelRow}: "Units in Pallet" is required when defaultPurchasingUnit is "pallet".`);
          skipped++;
          continue;
        }
        if (sellingPricePerPallet == null || sellingPricePerPallet < 0) {
          errors.push(
            `Row ${excelRow}: "Selling Price per Pallet ($)" is required when defaultPurchasingUnit is "pallet".`,
          );
          skipped++;
          continue;
        }
      }

      products.push({
        name,
        displayName: get('displayName') ? String(get('displayName')) : undefined,
        primaryBarcode: primaryBarcodeRaw,
        additionalBarcodes,
        sku: get('sku') ? String(get('sku')) : undefined,
        sellingPrice: productSellingPrice,
        cost: parseNum(get('cost')),
        crvFee: parseNum(get('crvFee')),
        expiryDate: parseExpiryDate(get('expiryDate')),
        batchNo1: colIdx('batchNo1') === -1 ? undefined : String(get('batchNo1') ?? '').trim() || null,
        expiryDate2: colIdx('expiryDate2') === -1 ? undefined : (parseExpiryDate(get('expiryDate2')) ?? null),
        batchNo2: colIdx('batchNo2') === -1 ? undefined : String(get('batchNo2') ?? '').trim() || null,
        vendors: vendors.length ? vendors : undefined,
        departmentName: normalizeCatalogName(get('departmentName')) || undefined,
        categoryNames: get('categoryNames')
          ? String(get('categoryNames')).split(',').map(normalizeCatalogName).filter(Boolean)
          : undefined,
        inStockQty: parseNum(get('inStockQty')),
        reorderThreshold: parseNum(get('reorderThreshold')),
        reorderQuantity: parseNum(get('reorderQuantity')),
        soldByWeight,
        ageVerification: parseBool(get('ageVerification')),
        ebtEligible: parseBool(get('ebtEligible'), true),
        couponEligible: parseBool(get('couponEligible'), true),
        taxable: parseBool(get('taxable'), true),
        excludeKitchenPrint,
        isVariablePrice: parseBool(get('isVariablePrice')),
        promptQuantity: parseBool(get('promptQuantity')),
        defaultSellingUnit,
        defaultPurchasingUnit,
        units: units.length ? units : undefined,
        building: get('building') ? String(get('building')) : undefined,
        aisle: get('aisle') ? String(get('aisle')) : undefined,
        bin: get('bin') ? String(get('bin')) : undefined,
        // NOTE: 'Modified' is deliberately not read back — the server stamps
        // last_changed_at, and updated_at is the registers' catalog cursor.
        customField1: get('customField1') ? String(get('customField1')) : undefined,
        customField2: get('customField2') ? String(get('customField2')) : undefined,
        customField3: get('customField3') ? String(get('customField3')) : undefined,
      });
    }
  }

  return { products, skipped, errors };
}
